import { useState, type FormEvent } from "react";
import Reveal from "@/components/base/Reveal";
import { instagramUrl } from "@/mocks/site";

const CONTACT_ENDPOINT = "https://readdy.ai/api/form/davpa6j2asjjtt1sku10";

const inputClass =
  "w-full rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400";

export default function Contact() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      return;
    }
    formData.delete("website_alt");

    const params = new URLSearchParams();
    formData.forEach((value, key) => {
      if (typeof value === "string") params.append(key, value);
    });

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(CONTACT_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });
      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message ||
        parsed?.message ||
        parsed?.meta?.detail ||
        responseText ||
        "Something went wrong. Please try again.";
      const isSpam = String(serverMsg).toLowerCase().includes("spam");

      if (response.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(String(serverMsg));
      }
    } catch {
      setStatus("error");
      setFormError("Network error. Please try again.");
    }
  }

  return (
    <>
      <section className="bg-foreground-950 px-4 pb-16 pt-32 text-center md:px-10 md:pb-20 md:pt-40">
        <Reveal>
          <p className="font-label text-xs tracking-[0.25em] uppercase text-accent-400">
            Get in Touch
          </p>
          <h1 className="mx-auto mt-5 max-w-3xl font-heading text-3xl font-extrabold uppercase tracking-tight text-background-50 md:text-5xl">
            Let's talk about your story.
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-base text-background-100/85">
            Tell us a little about your project and we'll be in touch within one business day.
          </p>
        </Reveal>
      </section>

      <section className="bg-background-50 px-4 py-16 md:px-10 md:py-24">
        <div className="mx-auto grid max-w-6xl grid-cols-1 gap-12 lg:grid-cols-[1fr_1.4fr] lg:gap-20">
          <Reveal>
            <h2 className="font-heading text-xl text-foreground-950">Studio</h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              We're based in Sonoma County, California, and travel wherever the story takes
              us.
            </p>
            <ul className="mt-8 space-y-6">
              <li className="flex items-start gap-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-accent-100 text-accent-800">
                  <i className="ri-mail-line" />
                </span>
                <div>
                  <p className="text-xs uppercase tracking-[0.15em] text-foreground-500">
                    Email
                  </p>
                  <a
                    href="mailto:hello@definitionfilmsllc.com"
                    className="text-sm text-foreground-900 hover:text-accent-700"
                  >
                    hello@definitionfilmsllc.com
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-accent-100 text-accent-800">
                  <i className="ri-map-pin-2-line" />
                </span>
                <div>
                  <p className="text-xs uppercase tracking-[0.15em] text-foreground-500">
                    Based in
                  </p>
                  <p className="text-sm text-foreground-900">Sonoma County, CA</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-accent-100 text-accent-800">
                  <i className="ri-time-line" />
                </span>
                <div>
                  <p className="text-xs uppercase tracking-[0.15em] text-foreground-500">
                    Response time
                  </p>
                  <p className="text-sm text-foreground-900">Within one business day</p>
                </div>
              </li>
            </ul>
            <a
              href={instagramUrl}
              target="_blank"
              rel="noreferrer"
              className="mt-8 inline-flex items-center gap-2 text-sm text-foreground-700 hover:text-accent-700"
            >
              <i className="ri-instagram-line text-lg" /> Follow @definitionfilms
            </a>
          </Reveal>

          <Reveal delay={120}>
            <form
              id="contact-form-df"
              data-readdy-form
              onSubmit={handleSubmit}
              className="rounded-lg border border-background-200 bg-background-100 p-6 md:p-8"
            >
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div>
                  <label htmlFor="cf-name" className="mb-2 block text-sm text-foreground-700">
                    Name
                  </label>
                  <input
                    id="cf-name"
                    name="name"
                    type="text"
                    required
                    autoComplete="name"
                    placeholder="Your name"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label htmlFor="cf-email" className="mb-2 block text-sm text-foreground-700">
                    Email
                  </label>
                  <input
                    id="cf-email"
                    name="email"
                    type="email"
                    required
                    autoComplete="email"
                    placeholder="you@company.com"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label htmlFor="cf-company" className="mb-2 block text-sm text-foreground-700">
                    Company
                  </label>
                  <input
                    id="cf-company"
                    name="company"
                    type="text"
                    autoComplete="organization"
                    placeholder="Company name"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label htmlFor="cf-type" className="mb-2 block text-sm text-foreground-700">
                    Project type
                  </label>
                  <select id="cf-type" name="project_type" className={inputClass} defaultValue="">
                    <option value="" disabled>
                      Select a type
                    </option>
                    <option value="Brand Film">Brand Film</option>
                    <option value="Commercial">Commercial</option>
                    <option value="Non-Profit Fundraising">Non-Profit Fundraising</option>
                    <option value="Social Content">Social Content</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="cf-budget" className="mb-2 block text-sm text-foreground-700">
                    Estimated budget
                  </label>
                  <select id="cf-budget" name="budget" className={inputClass} defaultValue="">
                    <option value="" disabled>
                      Select a range
                    </option>
                    <option value="Under $10k">Under $10k</option>
                    <option value="$10k – $25k">$10k – $25k</option>
                    <option value="$25k – $50k">$25k – $50k</option>
                    <option value="$50k+">$50k+</option>
                    <option value="Not sure yet">Not sure yet</option>
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="cf-message" className="mb-2 block text-sm text-foreground-700">
                    Tell us about your story
                  </label>
                  <textarea
                    id="cf-message"
                    name="message"
                    rows={5}
                    maxLength={500}
                    required
                    placeholder="What are you hoping to create?"
                    className={`${inputClass} resize-none`}
                  />
                </div>
              </div>

              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="form-aux-field"
              />

              <button
                type="submit"
                disabled={status === "submitting"}
                className="mt-6 w-full whitespace-nowrap rounded-md bg-primary-500 px-6 py-3.5 font-label text-xs tracking-[0.18em] uppercase text-background-50 transition-colors duration-200 hover:bg-primary-600 disabled:opacity-60 sm:w-auto cursor-pointer"
              >
                {status === "submitting" ? "Sending" : "Send Message"}
              </button>

              {status === "success" && (
                <p className="mt-4 rounded-md bg-accent-100 px-4 py-3 text-sm text-accent-900">
                  Thank you! Your message has been sent — we'll be in touch shortly.
                </p>
              )}
              {status === "error" && (
                <p className="mt-4 rounded-md bg-secondary-200 px-4 py-3 text-sm text-foreground-900">
                  {formError}
                </p>
              )}
            </form>
          </Reveal>
        </div>
      </section>
    </>
  );
}